import useLocalBufferPort from '@UI/Providers/PortsProvider/Hooks/LocalBuffer/useLocalBufferPort';
import * as React from 'react';

const POLL_INTERVAL_MS = 30_000;
const USAGE_RATIO_WARNING_THRESHOLD = 0.85;
/** Only used when the quota itself is unknown (`quotaBytes === null`): a conservative line, well past
 * what a single interview needs — voice in Opus runs roughly 10-15 MB/hour (D-14). */
const UNKNOWN_QUOTA_WARNING_THRESHOLD_BYTES = 200 * 1024 * 1024;

/**
 * Polls `LocalBufferPort.estimate()`/`totalBytes()` on an interval rather than once at mount: the
 * quota is shared with everything else on the device, and a capture can run for hours. Feeds a
 * discreet warning only — never interrupts the capture itself (`guides/device-ports.md`).
 */
function useStorageWarning(isActive: boolean): boolean {
	const localBufferPort = useLocalBufferPort();
	const [isLow, setIsLow] = React.useState(false);

	React.useEffect(() => {
		if (!isActive) {
			setIsLow(false);
			return undefined;
		}

		let isCancelled = false;

		const checkStorage = (): void => {
			void Promise.all([localBufferPort.estimate(), localBufferPort.totalBytes()]).then(
				([estimate, totalBytes]) => {
					if (isCancelled) {
						return;
					}

					if (estimate.quotaBytes !== null) {
						setIsLow(
							estimate.usageBytes !== null &&
								estimate.usageBytes / estimate.quotaBytes > USAGE_RATIO_WARNING_THRESHOLD,
						);
						return;
					}

					setIsLow(totalBytes > UNKNOWN_QUOTA_WARNING_THRESHOLD_BYTES);
				},
			);
		};

		checkStorage();
		const timer = window.setInterval(checkStorage, POLL_INTERVAL_MS);

		return () => {
			isCancelled = true;
			window.clearInterval(timer);
		};
	}, [isActive, localBufferPort]);

	return isLow;
}

export default useStorageWarning;
